import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DOMPurify from "dompurify"
import { SendHorizontal, Paperclip, SmilePlus } from "lucide-react"
import { toast } from "react-hot-toast"

import { useSessionContext } from '@/state/session-context'
import { getFeathersClient } from "./feathers-client"
import { ImageUploader } from "@/components/image-uploader"

import { useUserStore } from "@/state/user-store"
import { useChatStore } from '../../state/chat-store'
import { getColorSet } from "@/theme/color"
import { EmojiPicker } from "@/components/emoji-picker"

type MessageInput2Props = {
	maxImages?: number;
	disabled?: boolean;
	allowAttachments?: boolean;
	onError?: (msg: string) => void;
}

function toPlainTextFromHtml(html: string): string {
	const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
	const div = document.createElement("div")
	div.innerHTML = clean
	let txt = div.textContent || div.innerText || ""
	txt = txt.replace(/\u00a0/g, " ").replace(/[ \t]+/g, " ")
	txt = txt.replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
	return txt
}

export function MessageInput2(props: MessageInput2Props) {
	const [text, setText] = useState("")
	const [images, setImages] = useState<File[]>([])
	const fileInputRef = useRef<HTMLInputElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const emojiPickerRef = useRef<HTMLDivElement>(null)

	const maxImages = props.maxImages ?? 4

	const { session } = useSessionContext()
	const user = useUserStore(state => state.user)

	const feathersClient = getFeathersClient()
	const messagesService = feathersClient.service('messages')

	const activeChannelId = useChatStore(state => state.activeChannelId)
	const activeChannel = useChatStore(state => state.activeChannel)

	const colorSet = getColorSet({ senderColor: user?.color })

	const isReadOnly = activeChannel?.isReadOnly && !user?.isDeveloper
	const isLimitedMembership = activeChannel?.isLimitedMembership ?? false
	const isDisabled = props.disabled || isReadOnly
	const allowAttachments = props.allowAttachments ?? activeChannel?.usersCanSendImages ?? true

	let placeholder = "Type a message..."
	if (isReadOnly)
		placeholder = "You cannot send messages in this channel"
	if (isLimitedMembership)
		placeholder = "Subscribe to get full access to this group"

	const [showEmojiPicker, setShowEmojiPicker] = useState(false)

	const canSend = useMemo(
		() => {
			if (!activeChannelId) return false
			if (!session) return false
			if (isDisabled) return false
	
			return (text.trim().length > 0 || (allowAttachments && images.length > 0))
		}, 
		[text, images, isDisabled, allowAttachments, activeChannelId, session])
	
	const pushError = useCallback(
		(msg: string) => {
			if (props.onError)
				props.onError(msg)
			else
				toast.error(msg)
			console.warn(msg)
		},
		[props])

	const addImages = useCallback(
		(files: FileList | File[]) => {
			const arr = Array.from(files)
			const newImages: File[] = []
			for (const f of arr) {
				if (!f.type.startsWith("image/")) {
					pushError("Only images are allowed as attachments.")
					return
				}
				newImages.push(f)
			}
			if (images.length + newImages.length > maxImages) {
				pushError(`You can attach up to ${maxImages} image(s).`)
				return
			}
			setImages(prev => [...prev, ...newImages])
		}, 
		[images.length, maxImages, pushError])

	const onPaste = useCallback(
		(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
			if (isDisabled) 	
				return

			const cd = e.clipboardData
			const files = cd.files
			const plain = cd.getData("text/plain") || ""
			const html = cd.getData("text/html") || ""

			if (files && files.length > 0) {
				if (!allowAttachments)
					return
				e.preventDefault()
				for (const f of Array.from(files)) {
					if (!f.type.startsWith("image/")) {
						pushError("Only images are allowed as attachments.")
						return
					}
				}
				addImages(files)
				return
			}

			const incoming = html ? toPlainTextFromHtml(html) : plain
			if (!incoming.trim()) {
				e.preventDefault()
				return
			}

			e.preventDefault()
			const ta = e.currentTarget
			const start = ta.selectionStart ?? text.length
			const end = ta.selectionEnd ?? text.length
			const next = (text.slice(0, start) + incoming + text.slice(end)).slice(0, 5000)
			setText(next)
			requestAnimationFrame(() => {
				const pos = start + incoming.length
				ta.selectionStart = ta.selectionEnd = pos
			})
		}, 
		[addImages, isDisabled, pushError, text, allowAttachments])

	const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		if (isDisabled) 
			return
		if (!allowAttachments) 
			return
		e.preventDefault()
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			for (const f of Array.from(e.dataTransfer.files)) {
				if (!f.type.startsWith("image/")) {
					pushError("Only images are allowed as attachments.")
					return
				}
			}
			addImages(e.dataTransfer.files)
		}
	}, [addImages, isDisabled, allowAttachments, pushError])

	const insertAtCursor = (text: string) => {
		const el = textareaRef.current
		if (!el) 
			return
		const start = el.selectionStart ?? el.value.length
		const end = el.selectionEnd ?? el.value.length
		const next = el.value.slice(0, start) + text + el.value.slice(end)
		setText(next)
		requestAnimationFrame(() => {
			const pos = start + text.length
			el.focus()
			el.setSelectionRange(pos, pos)
		})	
	}

	const sendMessage = useCallback(async () => {
		if (!activeChannelId) return
		if (!session) return

		const sendSingleMessage = async (text?: string, attachmentId?: string) => {
			if (!session) return
			if (!activeChannelId) return
	
			const userId = session.userId
			const message = {
				channelId: activeChannelId,
				senderType: 'user',
				senderId: userId,
				senderName: user?.name,
				text,
				attachment: attachmentId ? { attachmentId } : undefined,
			}
	
			try {
				await messagesService.create(message)
			} catch (error) {
				console.error(error)
				const detail = error instanceof Error ? error.message : 'unknown error'
				pushError(`Failed to send message: ${detail}`)
			}
		}

		const imageUploader = new ImageUploader()
			
		try {
			//	TODO show progress bar

			const attachmentIds: string[] = []
			for (const image of images) {
				const attachmentId = await imageUploader.uploadImage(image)
				attachmentIds.push(attachmentId)
			}

			if (text.trim().length > 0)
				await sendSingleMessage(text)

			for (const attachmentId of attachmentIds) {
				await sendSingleMessage(undefined, attachmentId)
			}
		} catch (error) {
			console.error('Error sending message:', error)
			pushError('Failed to send message. Please try again.')
		}

		setText('')
		setImages([])
	}, [activeChannelId, text, session, images, pushError, messagesService, user?.name])

	const onSendClick = useCallback(
		() => {
			if (!canSend) 
				return
			sendMessage()
			setText("")
			setImages([])
		}, 
		[canSend, sendMessage, setText, setImages])

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault()
				onSendClick()
			}
		}, 
		[onSendClick])

	const removeImage = useCallback(
		(idx: number) => {
			setImages(prev => prev.filter((_, i) => i !== idx))
		}, 
		[])

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
				setShowEmojiPicker(false)
			}
		}

		if (showEmojiPicker) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showEmojiPicker])
	
	return (
		<div
			className="w-full flex flex-col gap-2"
			onDragOver={e => e.preventDefault()}
			onDrop={onDrop}
		>
			{images.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{images.map(
						(file, i) => {
							const url = URL.createObjectURL(file)
							return (
								<div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
									<img src={url} alt={`attachment-${i}`} className="w-full h-full object-cover" />
									<button
										type="button"
										className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1 rounded"
										onClick={() => removeImage(i)}
										aria-label="Remove image"
									>
										✕
									</button>
								</div>
							)
						}
					)}
				</div>
			)}

			<div className="flex gap-3 items-center">
				{allowAttachments && (
					<>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							multiple
							hidden
							onChange={e => {
								if (e.target.files) 
									addImages(e.target.files)
								if (fileInputRef.current) 
									fileInputRef.current.value = ""
							}}
						/>
						<button
							type="button"
							className="disabled:opacity-50"
							onClick={() => fileInputRef.current?.click()}
							disabled={isDisabled || images.length >= maxImages}
							title="Add image"
							style={{ color: colorSet.dark }}
						>
							<Paperclip className="h-5 w-5" />
						</button>
					</>
				)}

				<div className="relative" ref={emojiPickerRef}>
					<button			
						className="disabled:opacity-50"
						type="button"
						onClick={() => setShowEmojiPicker(!showEmojiPicker)}
						disabled={isDisabled}
						title="Emoji"
						style={{ color: colorSet.dark }}
					>
						<SmilePlus className="h-5 w-5 mt-1.5" />
					</button>

					{showEmojiPicker &&  (
						<div className="absolute bottom-full left-0 z-50 mb-2">
							<EmojiPicker
								onPick={(shortcode, emojiChar) => {
									insertAtCursor(`${emojiChar}`)
									setShowEmojiPicker(false)
								}}
							/>
						</div>
					)}
				</div>

				<textarea
					ref={textareaRef}
					className="flex-1 rounded-lg border p-2 px-4 outline-none min-h-[40px] shadow-messageInput disabled:opacity-50"
					placeholder={placeholder}
					value={text}
					onChange={e => setText(e.target.value)}
					onPaste={onPaste}
					onKeyDown={onKeyDown}
					disabled={isDisabled}
					maxLength={1000}
					rows={1}
					style={{
						backgroundColor: colorSet.background,
						borderColor: colorSet.border,
						color: colorSet.text,
						fontSize: '16px'
					}}
				/>

				<button
					className="disabled:opacity-50"
					onClick={onSendClick}
					disabled={!canSend}
					style={{ color: colorSet.dark }}
				>
					<SendHorizontal className="h-5 w-5" />
				</button>
			</div>
		</div>
	)
}